import moment from "moment";
import { Link } from "react-router-dom";
import { formatMoney } from "../../../../features/utils/formatMoney";
import { getVoidReviewGuidance } from "../../../../data/flight/order/getVoidAdminGuidance";

const TYPE_LABEL = {
  ADT: "Adult",
  Adult: "Adult",
  CHD: "Child",
  CNN: "Child",
  Child: "Child",
  INF: "Infant",
  INS: "Infant",
  Infant: "Infant",
};

export function normalizeVoidOrder(data) {
  if (!data) return data;
  const booking = Array.isArray(data.flightBooking)
    ? data.flightBooking[0]
    : data.flightBooking || data.flightObj;
  return { ...data, flightBooking: booking };
}

function Row({ label, value, note, warn }) {
  if (!value && !note) return null;
  return (
    <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr] gap-2 py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-xs text-gray-500 pt-0.5">{label}</div>
      <div className="flex flex-col gap-0.5 min-w-0">
        {value ? (
          <div className="text-sm text-gray-900 break-words">{value}</div>
        ) : null}
        {note ? (
          <div
            className={`text-xs ${warn ? "text-amber-700" : "text-gray-500"}`}
          >
            {note}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function getSelectedTravelers(data) {
  const ids = data?.passengerIds || [];
  const booking = data?.flightBooking || {};
  const travelers = booking?.travelers || [];
  if (!ids.length) return travelers;
  return travelers.filter((t) => ids.includes(t?.id));
}

function getTicketNumbers(travelers) {
  return travelers.flatMap((t) => t?.ticketNumbers || []).filter(Boolean);
}

function getFirstDeparture(data) {
  const dest = data?.flightBooking?.destinations?.[0]?.departureDate;
  if (dest) {
    const parsed = moment(dest);
    if (parsed.isValid()) return parsed;
  }
  const flight = data?.flightBooking?.flights?.[0];
  const raw = flight?.departureDate;
  if (!raw) return null;
  const parsed = moment(raw);
  return parsed.isValid() ? parsed : null;
}

function getRoute(data) {
  const flights = data?.flightBooking?.flights || [];
  if (!flights.length) return "-";
  const first = flights[0];
  const last = flights[flights.length - 1];
  const from = first?.departureLocation || first?.departure?.location;
  const to = last?.arrivalLocation || last?.arrival?.location;
  if (!from || !to) return "-";
  return `${from} → ${to}`;
}

export function VoidReviewFacts({ data, showQuestions = true }) {
  const order = normalizeVoidOrder(data) || {};
  const booking = order?.flightBooking || {};
  const agent = order?.account || {};
  const travelers = getSelectedTravelers(order);
  const tickets = getTicketNumbers(travelers);
  const departure = getFirstDeparture(order);
  const alreadyTravelled = departure
    ? departure.isBefore(moment(), "day")
    : false;
  const bookingStatus = booking?.status || "-";
  const issued =
    String(bookingStatus).toLowerCase() === "issued" || tickets.length > 0;
  const voidDeadlineRaw = booking?.voidDeadline || order?.voidDeadline;
  const voidDeadline = voidDeadlineRaw ? moment(voidDeadlineRaw) : null;
  const voidWindowOpen =
    voidDeadline && voidDeadline.isValid()
      ? voidDeadline.isAfter(moment())
      : null;
  const bookingId = booking?.bookingId || order?.bookingId;
  const pnr = booking?.pnr || "-";

  const passengerLine = travelers.length
    ? travelers
        .map((t) => {
          const type = TYPE_LABEL[t?.travelerType] || t?.travelerType || "";
          const name = [t?.firstName, t?.lastName].filter(Boolean).join(" ");
          return type ? `${name} (${type})` : name;
        })
        .join(", ")
    : "No passengers on this booking";

  return (
    <div className="flex flex-col">
      <div className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3">
        <Row
          label="Ticket"
          value={
            tickets.length
              ? `${bookingStatus} · ${tickets.join(" | ")}`
              : bookingStatus
          }
          note={
            issued
              ? `PNR ${pnr}`
              : "No ticket number found. Confirm this booking was issued before you accept."
          }
          warn={!issued}
        />
        <Row
          label="Passengers"
          value={passengerLine}
          note={
            travelers.length
              ? `${travelers.length} passenger${travelers.length > 1 ? "s" : ""} on this void`
              : "Nobody is selected. Do not accept."
          }
          warn={!travelers.length}
        />
        <Row
          label="Flight"
          value={`${getRoute(order)}${
            departure ? ` · ${departure.format("D MMM YYYY")}` : ""
          }`}
          note={
            alreadyTravelled
              ? "Travel date has passed. A void is usually no longer possible."
              : null
          }
          warn={alreadyTravelled}
        />
        <Row
          label="Void window"
          value={
            voidDeadline && voidDeadline.isValid()
              ? voidDeadline.format("D MMM YYYY HH:mm")
              : "Not on file"
          }
          note={
            voidWindowOpen === false
              ? "The void window looks closed. Check with the airline before you accept."
              : voidWindowOpen
                ? "Still inside the void window."
                : "Confirm the void period with the airline if you are unsure."
          }
          warn={voidWindowOpen === false}
        />
        <Row
          label="Void fee"
          value={
            order?.fee || order?.fee === 0 ? formatMoney(order?.fee) : "-"
          }
        />
        <Row
          label="Reason"
          value={order?.reason || "-"}
          note={order?.remark || null}
        />
        <Row
          label="Agent"
          value={
            [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") ||
            "-"
          }
          note={agent?.email || agent?.phone || null}
        />
      </div>

      {bookingId ? (
        <Link
          to={`/order/flight/ticket/detail/${bookingId}?page=Bookings`}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-3 text-sm font-medium text-primary1 hover:underline"
        >
          Open original booking
        </Link>
      ) : null}

      {showQuestions ? (
        <div className="mt-4 text-sm text-gray-700">
          <div className="font-medium text-gray-900 mb-1">Ask yourself</div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Is this the right issued ticket, and is it still unused?</li>
            <li>Are we still inside the airline void window?</li>
            <li>Should Intraverse take this void, or should it be declined?</li>
          </ol>
        </div>
      ) : null}
    </div>
  );
}

export default function VoidReviewCard({ data }) {
  if (!data) return null;
  const review = getVoidReviewGuidance(data);

  return (
    <div className="w-full rounded-xl mt-3 border border-gray-200 bg-white px-4 py-4">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">{review.title}</h3>
        {review.statusDisplay ? (
          <p className="text-xs font-medium text-gray-500 mt-1">
            Status: {review.statusDisplay}
          </p>
        ) : null}
        <p className="text-sm text-gray-600 mt-1">{review.why}</p>
      </div>
      {review.steps?.length ? (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
          <div className="text-sm font-semibold text-amber-900">Do this</div>
          <ol className="mt-1 list-decimal pl-5 space-y-1 text-sm text-gray-800">
            {review.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}
      <VoidReviewFacts data={data} showQuestions={false} />
    </div>
  );
}
